import { t } from '../i18n'
import type { BomRowSample, BomSummary } from '../types'

const MAX_BOM_BYTES = 5 * 1024 * 1024

const HEADER_ALIASES = {
  designator: [
    'designator', 'designators', 'ref', 'refs', 'reference', 'references',
    'partreference', 'component', 'components',
  ],
  qty: [
    'qty', 'quantity', 'qnty', 'amount', 'count', 'qtty',
    'qtyper', 'qtyperassembly', 'quantityper', 'perassembly',
  ],
  mpn: [
    'mpn', 'manufacturerpartnumber', 'manufacturerpart', 'partnumber',
    'part', 'mfrpartnumber', 'mfrpart', 'supplierpartnumber',
    'partnumber1', 'partnumber2', 'partnumber3', 'mpn1',
    'manufacturerpartnumber1', 'manufacturernumber',
  ],
  packageName: ['package', 'footprint', 'case', 'packagetype', 'casepackage', 'pkg'],
  description: ['description', 'desc', 'value', 'comment'],
  unitPrice: ['unitprice', 'price', 'unitcost', 'cost', 'unitpriceusd', 'upusd', 'usd'],
} as const

/**
 * Small CSV parser with quoted-field support. Handles commas, CRLF and escaped
 * quotes. BOM exports from Excel/Google Sheets are usually this shape.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        field += char
      }
      continue
    }

    if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      row.push(field)
      field = ''
    } else if (char === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else if (char !== '\r') {
      field += char
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }

  return rows.filter((candidate) => candidate.some((cell) => cell.trim() !== ''))
}

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function findColumn(headers: string[], aliases: readonly string[]): number {
  return headers.findIndex((header) => aliases.includes(header))
}

function parseQty(value: string, designators: string): number {
  // Only trust a purely numeric quantity. Values such as "267mm" or
  // "As Required" are wire lengths / notes, not placement counts.
  const numeric = value.trim().replace(/,/g, '')
  if (/^\d+(\.\d+)?$/.test(numeric)) {
    const direct = Number(numeric)
    if (Number.isFinite(direct) && direct > 0) return Math.round(direct)
  }

  const refs = designators
    .split(/[,;/\s]+/)
    .map((ref) => ref.trim())
    .filter(Boolean)
  return refs.length > 0 ? refs.length : 1
}

function parsePrice(value: string): number | null {
  const raw = value.trim()
  if (!raw) return null
  // "$350/50pcs", "$60 / 50 pcs", "26%" are totals/notes, not unit prices.
  if (raw.includes('/') || raw.includes('%')) return null
  if (/[a-z]/i.test(raw.replace(/\b(usd|rmb|cny|eur)\b/gi, ''))) return null

  let cleaned = raw.replace(/[^0-9.,\-]/g, '')
  if (!cleaned) return null
  if (cleaned.includes('.')) {
    cleaned = cleaned.replace(/,/g, '')
  } else if ((cleaned.match(/,/g) ?? []).length === 1) {
    // A single comma is a decimal separator in most non-US BOM exports.
    cleaned = cleaned.replace(',', '.')
  } else {
    cleaned = cleaned.replace(/,/g, '')
  }
  if (cleaned === '-' || cleaned === '.') return null

  const price = Number(cleaned)
  return Number.isFinite(price) && price >= 0 ? price : null
}

/** A real BOM line has a designator (or, in designator-less files, a quantity). */
function isBomRow(row: string[], designatorIndex: number, qtyIndex: number): boolean {
  if (designatorIndex >= 0) return (row[designatorIndex] ?? '').trim() !== ''
  return qtyIndex >= 0 && /^\d+(\.\d+)?$/.test((row[qtyIndex] ?? '').trim().replace(/,/g, ''))
}

/**
 * Pick the unit-price column with the most priced BOM lines. Exports often
 * carry several volume tiers (x50 / x100 / x500); notes and footer totals are
 * ignored because they are not BOM lines. Ties keep the earlier tier, which is
 * the more conservative (higher) unit price.
 */
function findPriceColumn(headers: string[], rows: string[][], designatorIndex: number, qtyIndex: number): number {
  const candidates = headers
    .map((header, index) => ((HEADER_ALIASES.unitPrice as readonly string[]).includes(header) ? index : -1))
    .filter((index) => index >= 0)
  let best = -1
  let bestCount = 0
  for (const index of candidates) {
    const count = rows
      .slice(1)
      .filter((row) => isBomRow(row, designatorIndex, qtyIndex) && parsePrice(row[index] ?? '') !== null)
      .length
    if (count > bestCount) {
      best = index
      bestCount = count
    }
  }
  return bestCount > 0 ? best : candidates[0] ?? -1
}

export function parseBom(text: string, fileName = 'bom.csv'): BomSummary {
  const rows = parseCsv(text)
  if (rows.length < 2) {
    throw new Error(t('error.bomEmpty'))
  }

  const headers = rows[0].map(normalizeHeader)
  const designatorIndex = findColumn(headers, HEADER_ALIASES.designator)
  const qtyIndex = findColumn(headers, HEADER_ALIASES.qty)
  const mpnIndex = findColumn(headers, HEADER_ALIASES.mpn)
  const packageIndex = findColumn(headers, HEADER_ALIASES.packageName)
  const descriptionIndex = findColumn(headers, HEADER_ALIASES.description)

  if (designatorIndex < 0 && qtyIndex < 0) {
    throw new Error(t('error.bomDesignator'))
  }

  const priceIndex = findPriceColumn(headers, rows, designatorIndex, qtyIndex)

  const unique = new Set<string>()
  const sample: BomRowSample[] = []
  const warnings: string[] = []
  let placements = 0
  let componentCost = 0
  let pricedRows = 0
  let totalRows = 0

  for (const row of rows.slice(1)) {
    // Footer rows (layers, material, tooling, price tables) sit below the BOM
    // and carry strays like "24.5" in price columns. Only count real lines.
    if (!isBomRow(row, designatorIndex, qtyIndex)) continue

    const designators = designatorIndex >= 0 ? row[designatorIndex]?.trim() ?? '' : ''
    const qty = parseQty(qtyIndex >= 0 ? row[qtyIndex] ?? '' : '', designators)
    const mpn = mpnIndex >= 0 ? row[mpnIndex]?.trim() ?? '' : ''
    const packageName = packageIndex >= 0 ? row[packageIndex]?.trim() ?? '' : ''
    const description = descriptionIndex >= 0 ? row[descriptionIndex]?.trim() ?? '' : ''
    const price = priceIndex >= 0 ? parsePrice(row[priceIndex] ?? '') : null

    totalRows += 1
    placements += qty
    unique.add(mpn || `${designators}|${description}|${packageName}`)

    if (price !== null) {
      componentCost += price * qty
      pricedRows += 1
    }

    if (sample.length < 5) {
      sample.push({ designator: designators || `ROW ${totalRows}`, qty, mpn: mpn || description, packageName })
    }
  }

  if (mpnIndex < 0) {
    warnings.push(t('bom.warnNoMpn'))
  }
  if (priceIndex < 0) {
    warnings.push(t('bom.warnNoPrice'))
  } else if (pricedRows < totalRows) {
    warnings.push(t('bom.warnPartialPrice'))
  }
  if (placements === 0) {
    warnings.push(t('bom.warnNoPlacements'))
  }

  return {
    fileName,
    rows: totalRows,
    uniqueParts: unique.size,
    placements,
    componentCost: priceIndex >= 0 && pricedRows > 0 ? Math.round(componentCost * 100) / 100 : null,
    sample,
    warnings,
  }
}

export async function readBomFile(file: File): Promise<BomSummary> {
  if (file.size > MAX_BOM_BYTES) {
    throw new Error(t('error.bomTooLarge'))
  }
  if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
    throw new Error(t('error.bomCsv'))
  }
  const text = await file.text()
  return parseBom(text, file.name)
}
